import { gql } from "lib/apolloClient";
import {
  organizationStudentFeedFragment,
  jobFeedFragment,
  subTaskFeedFragment,
  organizationStaffFeedFragment,
  organizationCourseFeedFragment,
  learningCourseFeedFragment,
} from "./SyncFragments";

export const subTaskFeedQuery = gql`
  query getSubTaskFeed(
    $id: ID!
    $filters: IntegrationSubTaskFeedFilterInput
    $first: Int
    $afterV2: String
  ) {
    node(id: $id, type: INTEGRATION_JOB) {
      id
      ... on IntegrationJob {
        ...subTaskFeedEdgeItem
      }
    }
  }
  ${subTaskFeedFragment.subTaskFeedEdge}
`;

export const getIntegrationOrgCurriculums = gql`
  query getIntegrationOrgCurriculums($input: IntegrationOrganizationInfoInput) {
    integration {
      getIntegrationOrganizationInfo(input: $input) {
        organizationId
        organizationName
        service
        curriculums
      }
    }
  }
`;
export const getExcludedParentsQuery = gql`
  query integrationFetchExcludedToddleParents(
    $input: IntegrationFetchExcludedToddleParentsInput!
  ) {
    integration {
      integrationFetchExcludedToddleParents(input: $input) {
        id
        firstName
        lastName
        middleName
        preferredName
        prefix
        suffix
        pronouns
        email
      }
      __typename
    }
  }
`;

export const getStudentFeedQuery = gql`
  query getStudentFeed(
    $organizationId: ID!
    $after: String
    $first: Int
    $filters: OrgConnectionFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $familiesFilter: FamiliesFiltersInput
    $academicYearId: ID
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationStudentFeedEdgeItem
      }
    }
  }
  ${organizationStudentFeedFragment.organizationStudentFeedEdge}
`;

export const getJobFeedQuery = gql`
  query getJobFeed(
    $id: ID!
    $afterV2: String
    $filters: OrganizationIntegrationJobFilterInput
    $first: Int
    $curriculumProgramId: ID
    $academicYearState: ACADEMIC_YEAR_STATE_ENUM
    $subTaskFilters: IntegrationSubTaskFeedFilterInput
    $fetchSubTasksCount: Boolean! = false
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationCourseFeedEdgeItem
      }
    }
  }
  ${jobFeedFragment.organizationJobFeedEdge}
`;

export const getAvailableServicesQuery = gql`
  query getAvailableServices(
    $id: ID!
    $filters: AvailableSyncServicesV2Filters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        availableSyncServicesV2(filters: $filters)
      }
    }
  }
`;

export const getThirdPartyMappingQuery = gql`
  query thirdPartyMappings($id: ID!, $input: ThirdPartyMappingInput!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        thirdPartyMappings(input: $input) {
          thirdPartyId
          toddleId
        }
      }
    }
  }
`;

export const getTeacherFeedQuery = gql`
  query getTeacherFeed(
    $organizationId: ID!
    $after: String
    $first: Int = 20000
    $filters: OrgConnectionFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $academicYearId: ID
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationStaffFeedEdgeItem
      }
    }
  }
  ${organizationStaffFeedFragment.organizationStaffFeedEdge}
`;

export const getCourseFeedQuery = gql`
  query getCourseFeed(
    $organizationId: ID!
    $after: String
    $first: Int
    $filters: CourseFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationCourseFeedEdgeItem
      }
    }
  }
  ${organizationCourseFeedFragment.organizationCourseFeedEdge}
`;

export const integrationTestSisConnectionQuery = gql`
  query integrationTestSisConnection(
    $input: IntegrationTestSisConnectionInput!
  ) {
    integration {
      integrationTestSisConnection(input: $input) {
        allow
        message
        __typename
      }
      __typename
    }
  }
`;

export const getOrganizationCurriculumProgramsQuery = gql`
  query getOrganizationCurriculumPrograms($organizationId: ID!) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms {
          id
          label
          acronym
          curriculum {
            id
            label
            type
          }
        }
      }
    }
  }
`;

export const getOrganizationSubjectGroupsQuery = gql`
  query getOrganizationSubjectGroups(
    $id: ID!
    $filters: SubjectGroupFilterInput
    $subjectFilters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjectGroups(filters: $filters) {
          id
          name
          subjects(filters: $subjectFilters) {
            id
            name
            icon
            ibOfferedSubject {
              id
              contentSubject {
                id
                assessmentYearTitle
              }
            }
          }
        }
      }
    }
  }
`;

export const getExcludedEntitiesQuery = gql`
  query getExcludedEntities($input: IntegrationFetchExcludedEntitiesInput!) {
    integration {
      integrationFetchExcludedEntities(input: $input) {
        key
        excludedIds
      }
    }
  }
`;

export const getEntityMappingsQuery = gql`
  query getEntityMappings(
    $input: IntegrationFetchSubjectAndGradeMappingsInput!
  ) {
    integration {
      integrationFetchSubjectAndGradeMappings(input: $input) {
        entityType
        result
      }
    }
  }
`;

export const getOAuthConfigDetailsQuery = gql`
  query getOAuthConfigDetails($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        oauthConfig {
          service
          metadata
        }
      }
    }
  }
`;

export const getLearningCoursesQuery = gql`
  query getLearningCoursesFeed(
    $curriculumProgramId: ID!
    $curriculumType: ENTITY_TYPE_ENUM!
    $after: String
    $orderByDirection: ORDER_BY_DIRECTION
    $orderBy: ORDER_BY_ENUM
    $filters: LearningCourseFilters
    $collaboratorFilters: LearningCourseCollaboratorFilters
    $excludeLearningCourseRelations: Boolean = false
  ) {
    node(id: $curriculumProgramId, type: $curriculumType) {
      id
      ... on IbPypCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on IbMypCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on UBDCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on IbDpCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }

      ... on GenericCurriculumProgram {
        id
        learningCourse(
          first: 20
          after: $after
          orderByDirection: $orderByDirection
          orderBy: $orderBy
          filters: $filters
        ) {
          edges {
            node {
              ...learningCourseItem
            }
          }
          count {
            teacherCourseCount
            bluePrintCourseCount
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${learningCourseFeedFragment}
`;

export const getOrganizationAcademicYearAcademicTermsQuery = gql`
  query getOrganizationAcademicYearTerms(
    $organizationId: ID!
    $academicTermFilters: TermFilters!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        academicYears {
          id
          startDate
          endDate
          academicTerms(filters: $academicTermFilters) {
            totalCount
            edges {
              node {
                id
                label
                startDate
                endDate
                type
              }
            }
          }
        }
      }
    }
  }
`;

export const getAcademicCoursesQuery = gql`
  query getAcademicCourses(
    $id: ID!
    $academicCourseFilters: AcademicCourseFilters
    $orderBy: ACADEMIC_COURSE_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        academicCourses(
          filters: $academicCourseFilters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          id
          label
          code
          learningCourses {
            edges {
              node {
                id
                title
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentAlertTypesQuery = gql`
  query getStudentAlertTypes($id: ID!, $filters: StudentAlertTypeFilters) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        studentAlertTypes(filters: $filters) {
          id
          name
        }
      }
    }
  }
`;

export const getJobFeedForOneSchoolFileUploadQuery = gql`
  query GetOneSchoolLatestJob(
    $id: ID!
    $afterV2: String
    $filters: OrganizationIntegrationJobFilterInput
    $first: Int
    $curriculumProgramId: ID
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        jobFeed(
          first: $first
          afterV2: $afterV2
          curriculumProgramId: $curriculumProgramId
          filters: $filters
        ) {
          totalCount
          edges {
            node {
              id
              status
              serviceName
              createdAt
              curriculumId
              s3Url
              metadata
              isLatest
              type
              createdBy {
                id
                firstName
                lastName
                email
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;
